import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fromJS } from "immutable";
import { PLATFORM_SHORT_TEXT } from "../constants/platform";
import InstagramPost from "./review/InstagramPost";
import FacebookPost from "./review/FacebookPost";
import LinkedInPost from "./review/LinkedInPost";
import TwitterPost from "./review/TwitterPost";
import ThreadsPost from "./review/ThreadsPost";
import { localDateTimeInTimeZone } from "../helpers";
import { getSocialPostEngagement } from "../../api/AIContentApi";

interface ReviewPostProps {
  postDetail: any;
  onToggleFacebookInsights?: () => void;
  onToggleThreadsInsights?: () => void;
  onToggleInstagramInsights?: () => void;
}

const ReviewPost = ({
  postDetail,
  onToggleFacebookInsights,
  onToggleThreadsInsights,
  onToggleInstagramInsights,
}: ReviewPostProps) => {
  const { projectId }: any = useParams();

  const id = postDetail.get("id");
  const scheduledTime = postDetail.getIn(["data", "scheduledTime", "value"]);
  const timezone = postDetail.getIn(["data", "timezone", "value"]);
  const platform = postDetail.getIn(["data", "platform", "value"]);
  const status = postDetail.getIn(["data", "status", "value"]);
  const summary = postDetail.getIn(["data", "summary", "value"]);
  const description = postDetail.getIn(["data", "description", "value"]);
  const postType = postDetail.getIn(["data", "postType", "value"]);
  const mediaId = postDetail.getIn(["data", "mediaId", "value"]);
  const mediaType = postDetail.getIn(["data", "mediaType", "value"]);

  const isPlatformShortText = PLATFORM_SHORT_TEXT.includes(platform);
  const content = isPlatformShortText ? summary : description;
  const scheduledDateTime = localDateTimeInTimeZone(scheduledTime, timezone);

  const { data: engagement } = useQuery({
    queryKey: ["social-post-engagement", id],
    queryFn: async () => {
      const data = await getSocialPostEngagement(projectId, id);
      return data;
    },
    enabled: !!projectId && !!id && status === "PUBLISHED",
  });

  if (platform === "instagram") {
    return (
      <InstagramPost
        postType={postType}
        content={content}
        scheduledDateTime={scheduledDateTime}
        mediaId={mediaId}
        mediaType={mediaType}
        engagement={fromJS(engagement || {})}
        onToggleInsights={onToggleInstagramInsights}
      />
    );
  } else if (platform === "facebook") {
    return (
      <FacebookPost
        postType={postType}
        content={content}
        scheduledDateTime={scheduledDateTime}
        mediaId={mediaId}
        mediaType={mediaType}
        engagement={fromJS(engagement || {})}
        platformPostId={engagement?.platformPostId}
        onToggleInsights={onToggleFacebookInsights}
      />
    );
  } else if (platform === "linkedin") {
    return (
      <LinkedInPost
        postType={postType}
        content={content}
        scheduledDateTime={scheduledDateTime}
        mediaId={mediaId}
        mediaType={mediaType}
        engagement={fromJS(engagement || {})}
      />
    );
  } else if (platform === "twitter") {
    return (
      <TwitterPost
        postType={postType}
        content={content}
        scheduledDateTime={scheduledDateTime}
        mediaId={mediaId}
        mediaType={mediaType}
        engagement={fromJS(engagement || {})}
      />
    );
  } else if (platform === "threads") {
    return (
      <ThreadsPost
        postType={postType}
        content={content}
        scheduledDateTime={scheduledDateTime}
        mediaId={mediaId}
        mediaType={mediaType}
        engagement={fromJS(engagement || {})}
        onToggleInsights={onToggleThreadsInsights}
      />
    );
  }
  return null;
};

export default ReviewPost;
